import { Store, UserRound } from "lucide-react";

import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaSurface } from "@/shared/components/layout/ColocaSurface";
import useTranslations from "@/shared/lang/useTranslations";

import type { BrebQrResolution } from "../types/brebQr.types";
import { isBusinessPayee, maskBrebKeyForDisplay } from "../utils/brebQrPayment";

export interface BrebQrPayeeCardProps {
  resolution: BrebQrResolution;
}

function Detail({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-2">
      <dt className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{label}</dt>
      <dd
        className={
          mono ? "truncate font-mono text-sm text-foreground" : "truncate text-sm font-medium text-foreground"
        }
      >
        {value}
      </dd>
    </div>
  );
}

/** Who receives, as the directory reports them — the confirmation every Bre-B app shows before paying. */
export function BrebQrPayeeCard({ resolution }: BrebQrPayeeCardProps) {
  const { t } = useTranslations("brebQrPay");
  const isBusiness = isBusinessPayee(resolution);
  const Icon = isBusiness ? Store : UserRound;

  return (
    <ColocaSurface padding="none" className="overflow-hidden">
      <div className="flex items-center gap-3 px-4 pt-4 pb-3">
        <span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Icon className="size-5" aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-primary/80">
            {t("payee_label")}
          </p>
          <p className="truncate text-lg leading-tight font-semibold text-foreground">
            {resolution.holder_name || "—"}
          </p>
          <p className="text-xs text-muted-foreground">
            {isBusiness ? t("payee_merchant") : t("payee_person")}
          </p>
        </div>
      </div>
      <div aria-hidden="true" className="border-t border-dashed border-border" />
      <dl className="divide-y divide-border/60 px-4 py-1">
        {resolution.bank_name && <Detail label={t("payee_bank")} value={resolution.bank_name} />}
        <Detail
          label={t("payee_key")}
          value={maskBrebKeyForDisplay(resolution.key_type, resolution.key_value)}
        />
        {resolution.id_number && (
          <Detail
            label={t("payee_document")}
            value={[resolution.id_type, resolution.id_number].filter(Boolean).join(" ")}
          />
        )}
        {resolution.id_qr && <Detail label={t("payee_reference")} value={resolution.id_qr} mono />}
      </dl>
      {resolution.status !== "ACTIVE" && (
        <div className="px-4 pb-4">
          <ColocaAlert
            inline
            variant="destructive"
            text={t("payee_inactive", { status: resolution.status })}
          />
        </div>
      )}
    </ColocaSurface>
  );
}
